import React, { Fragment, useEffect, useState } from "react";
import { useArgusCart } from "../../contexts/ArgusCartContext";
import {
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  Paper,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import SubdirectoryArrowLeftIcon from "@mui/icons-material/SubdirectoryArrowLeft";
import DoneIcon from "@mui/icons-material/Done";
import { find } from "lodash";
import { COLORS } from "../../constants";
import useSystemHook from "./../../hooks/SystemHook";

const AddedToCart = () => {
  const {
    addedToCartViewOpen,
    handleCloseAddedToCartView,
    itemAddedToCart,
    items,
    updateItemQuantity,
  } = useArgusCart();

  const [cartProduct, setCartProduct] = useState(null);
  const [errorMessages, setErrorMessages] = useState({});

  const { isMobile, isTablet, isDesktop } = useSystemHook();

  useEffect(() => {
    if (!itemAddedToCart || !items) {
      return;
    }

    const id = itemAddedToCart.id;
    const cartProductTemp = find(items, (x) => x.id === id);
    setCartProduct(cartProductTemp);
  }, [items, itemAddedToCart]);

  useEffect(() => {}, [cartProduct]);

  const handleQuantityChange = (id, sku, value) => {
    const quantity = parseInt(value);

    if (isNaN(quantity) || quantity <= 0) {
      setErrorMessages((prev) => ({
        ...prev,
        [sku]: "Quantity must be a positive number.",
      }));
    } else if (quantity > 500) {
      setErrorMessages((prev) => ({
        ...prev,
        [sku]:
          "For quantities greater than 500, please contact our sales team.",
      }));
    } else {
      setErrorMessages((prev) => ({
        ...prev,
        [sku]: "",
      }));
      updateItemQuantity(id, quantity);
    }
  };

  const handleIncrement = (id, sku, currentQuantity) => {
    if (currentQuantity < 500) {
      updateItemQuantity(id, currentQuantity + 1);
      setErrorMessages((prev) => ({
        ...prev,
        [sku]: "",
      }));
    }
  };

  const handleDecrement = (id, sku, currentQuantity) => {
    if (currentQuantity > 1) {
      updateItemQuantity(id, currentQuantity - 1);
      setErrorMessages((prev) => ({
        ...prev,
        [sku]: "",
      }));
    }
  };

  return (
    <Fragment>
      {!!addedToCartViewOpen && (
        <Drawer
          anchor="right"
          open={addedToCartViewOpen}
          onClose={handleCloseAddedToCartView}
          PaperProps={{
            sx: {
              width: !isMobile ? "35%" : "100%",
              backgroundColor: COLORS.background + "!important",
              display: "flex",
              flexDirection: "column",
              height: "100%", // Ensure full height for flex layout
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              p: 2,
              backgroundColor: COLORS.white,
              borderBottom: `1px solid ${COLORS.border}`,
            }}
          >
            <Button
              variant="text"
              size="small"
              startIcon={<CloseIcon fontSize="large" />}
              onClick={handleCloseAddedToCartView}
              sx={{
                textTransform: "uppercase",
                color: COLORS.themeBlack,
                fontWeight: 500,
              }}
            >
              close
            </Button>
          </Box>

          <Box sx={{ p: 2, flexGrow: 1 }}>
            {" "}
            {/* Flex grow to push buttons down */}
            <Typography
              sx={{ fontWeight: 500, fontSize: "var(--font-size-xs)" }}
            >
              <DoneIcon
                sx={{
                  color: COLORS.themeGreen,
                  fontSize: "var(--font-size-m)",
                  verticalAlign: "middle",
                  mr: 0.5,
                }}
              />
              Added to cart
            </Typography>
            <Paper
              elevation={3}
              sx={{
                display: "flex",
                p: 1,
                my: 2,
                borderRadius: "2px",
                border: `1px solid ${COLORS.border}`,
                boxShadow: "none",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  src={itemAddedToCart.image}
                  alt={itemAddedToCart.name}
                  variant="square"
                  sx={{
                    width: !isMobile ? 150 : 100,
                    height: "100%", // Stretch to full height
                    mr: 2,
                    "& .MuiAvatar-img": {
                      objectFit: "contain",
                      transform: "scale(1.1)", // optional zoom control
                    },
                  }}
                />
              </Box>

              {/* Quantity Input */}
              {!!cartProduct && (
                <Box xs={6} sm={4} md={2} sx={{ mt: 2 }}>
                  <Box>
                    <Typography
                      fontWeight="500"
                      sx={{ fontSize: "var(--font-size-xs)" }}
                    >
                      {itemAddedToCart.name}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        mr: 1,
                        my: 0.5,
                        fontWeight: 300,
                        fontSize: "var(--font-size-xxs)",
                      }}
                    >
                      SKU:
                    </Typography>
                    <Typography
                      sx={{ fontWeight: 300, fontSize: "var(--font-size-xxs)" }}
                    >
                      {itemAddedToCart.sku}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        mt: -1,
                      }}
                    >
                      <Typography
                        sx={{
                          mr: 2.3,
                          fontWeight: 300,
                          fontSize: "var(--font-size-xxs)",
                        }}
                      >
                        QTY:
                      </Typography>
                      <SubdirectoryArrowLeftIcon
                        sx={{
                          transform: "rotate(-90deg)",
                          fontSize: "1.5rem",
                          mt: 1,
                          color: COLORS.darkText,
                        }}
                      />
                    </Box>
                  </Box>
                  <TextField
                    variant="outlined"
                    type="number"
                    value={cartProduct.quantity}
                    size="small"
                    sx={{
                      mt: 1,
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          border: "none",
                        },
                        "&:hover fieldset": {
                          border: "none",
                        },
                        "&.Mui-focused fieldset": {
                          border: "none",
                        },
                        backgroundColor: COLORS.background,
                        borderRadius: "2px",
                      },
                    }}
                    onChange={(e) =>
                      handleQuantityChange(
                        itemAddedToCart.id,
                        itemAddedToCart.sku,
                        e.target.value
                      )
                    }
                    inputProps={{
                      min: 1,
                      max: 500,
                      step: 1,
                      "aria-label": "quantity",
                      style: {
                        textAlign: "center",
                        fontSize: "0.875rem",
                      },
                    }}
                    error={!!errorMessages[itemAddedToCart.sku]}
                    helperText={errorMessages[itemAddedToCart.sku]}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            onClick={() =>
                              handleDecrement(
                                itemAddedToCart.id,
                                itemAddedToCart.sku,
                                cartProduct.quantity
                              )
                            }
                            disabled={cartProduct.quantity <= 1}
                            sx={{ padding: 0 }}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              handleIncrement(
                                itemAddedToCart.id,
                                itemAddedToCart.sku,
                                cartProduct.quantity
                              )
                            }
                            disabled={cartProduct.quantity >= 500}
                            sx={{ padding: 0 }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}
            </Paper>
          </Box>

          {/* Button Container at the Bottom */}
          <Box
            sx={{
              backgroundColor: COLORS.white,
              borderTop: `1px solid ${COLORS.border}`,
              p: 2,
            }}
          >
            <Box
              sx={{
                backgroundColor: COLORS.themeBlack,
                border: `1px solid ${COLORS.themeBlack}`,
                p: 1,
                mb: 1,
              }}
            >
              <Button
                fullWidth
                href="/cart"
                size="small"
                startIcon={<ShoppingCartIcon fontSize="small" />}
                sx={{
                  color: COLORS.white,
                  "& .MuiSvgIcon-root": {
                    color: COLORS.white,
                  },
                  fontSize: "var(--font-size-xxs)",
                }}
              >
                Go to cart
              </Button>
            </Box>
            <Box
              sx={{
                backgroundColor: COLORS.white,
                border: `1px solid ${COLORS.themeBlack}`,
                p: 1,
              }}
            >
              <Button
                fullWidth
                onClick={handleCloseAddedToCartView}
                size="small"
                sx={{
                  color: COLORS.themeBlack,
                  fontSize: "var(--font-size-xxs)",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Continue shopping
              </Button>
            </Box>
          </Box>
        </Drawer>
      )}
    </Fragment>
  );
};
export default AddedToCart;
